// src/pages/Home.js
import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { getUserTrips } from '../services/tripService';
import { getTripPlaces } from '../services/tripService';
import TripCard from '../components/TripCard';
import './Home.css';

const Home = ({ user }) => {
  const [trips, setTrips] = useState([]);
  const [tripsWithPlaces, setTripsWithPlaces] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');

  useEffect(() => {
    fetchUserTrips();
  }, [user]);

  const fetchUserTrips = async () => {
    try {
      setLoading(true);
      const userTrips = await getUserTrips(user.uid);
      
      // 為每個行程獲取景點數據以計算總費用和評分
      const tripsWithPlacesData = await Promise.all(
        userTrips.map(async (trip) => {
          try {
            const places = await getTripPlaces(trip.id);
            
            // 計算實際總費用
            const actualTotalCost = places.reduce((total, place) => {
              if (place.expense) {
                const placeTotal = Object.values(place.expense).reduce((sum, cost) => sum + (Number(cost) || 0), 0);
                return total + placeTotal;
              }
              return total;
            }, 0);
            
            // 計算創建者平均評分
            const ratedPlaces = places.filter(p => Number(p.rating) > 0);
            const averageRating = ratedPlaces.length > 0 
              ? (ratedPlaces.reduce((sum, p) => sum + Number(p.rating), 0) / ratedPlaces.length).toFixed(1)
              : null;
            
            return {
              ...trip,
              places,
              actualTotalCost,
              creatorAverageRating: averageRating
            };
          } catch (error) {
            console.error(`Error fetching places for trip ${trip.id}:`, error);
            return {
              ...trip,
              places: [],
              actualTotalCost: 0,
              creatorAverageRating: null
            };
          }
        })
      );
      
      setTrips(userTrips);
      setTripsWithPlaces(tripsWithPlacesData);
    } catch (error) {
      setError('無法載入行程資料');
      console.error('Error fetching trips:', error);
    } finally {
      setLoading(false);
    }
  };

  const completedTrips = tripsWithPlaces.filter(trip => trip.isCompleted);
  const ongoingTrips = tripsWithPlaces.filter(trip => !trip.isCompleted);

  if (loading) {
    return (
      <div className="home-container">
        <div className="loading">載入中...</div>
      </div>
    );
  }

  return (
    <div className="home-container">
      <div className="home-header">
        <h1>我的行程</h1>
        <Link to="/new-trip" className="new-trip-btn">
          + 新增行程
        </Link>
      </div>

      {error && (
        <div className="error-message">
          {error}
        </div>
      )}

      <div className="trips-layout">
        {/* 進行中/未開始的行程 */}
        <div className="trips-section ongoing-trips">
          <h2>進行中 / 即將開始</h2>
          {ongoingTrips.length === 0 ? (
            <div className="no-trips">
              <p>沒有進行中的行程</p>
              <Link to="/new-trip" className="create-first-trip">
                建立你的第一個行程
              </Link>
            </div>
          ) : (
            <div className="trips-grid">
              {ongoingTrips.map(trip => (
                <TripCard 
                  key={trip.id} 
                  trip={trip} 
                  onUpdate={fetchUserTrips}
                />
              ))}
            </div>
          )}
        </div>

        {/* 已完成的行程 */}
        <div className="trips-section completed-trips">
          <h2>已完成的行程</h2>
          {completedTrips.length === 0 ? (
            <div className="no-trips">
              <p>沒有已完成的行程</p>
            </div>
          ) : (
            <div className="trips-grid">
              {completedTrips.map(trip => (
                <TripCard 
                  key={trip.id} 
                  trip={trip} 
                  onUpdate={fetchUserTrips}
                />
              ))}
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default Home;